/**
 * Thin AgentCore API client used by the Office add-in. Targets the same
 * REST API that the main AgentCore web frontend uses.
 */

import { getIdToken } from './auth';

const RAW_API_BASE_URL = import.meta.env.VITE_API_URL;
if (!RAW_API_BASE_URL) {
  // eslint-disable-next-line no-console
  console.error('VITE_API_URL is not set for the Office add-in build.');
}
export const API_BASE_URL = (RAW_API_BASE_URL || '').replace(/\/+$/, '');

const AUTO_CHAT_MODE = (import.meta.env.VITE_AUTO_CHAT_MODE || 'orchestrator').toLowerCase();

/** Auto mode uses POST /autonomy/chat (Brain: skills, agents, direct answers). */
export const USE_AUTONOMY_CHAT = AUTO_CHAT_MODE === 'autonomy';

/** Auto mode uses legacy POST /orchestrator/chat (multi-agent synthesis). */
export const USE_ORCHESTRATOR_CHAT = !USE_AUTONOMY_CHAT;

interface ApiResult<T> {
  data?: T;
  error?: string;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  const token = await getIdToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string>) || {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), 120_000);
  const extSignal = init.signal;
  if (extSignal) {
    if (extSignal.aborted) ctrl.abort();
    else extSignal.addEventListener('abort', () => ctrl.abort(), { once: true });
  }

  try {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers,
      signal: ctrl.signal,
    });
    const text = await res.text();
    const json = text ? safeJson(text) : {};
    if (!res.ok) {
      const err = (json as { error?: string; details?: string }).error || `HTTP ${res.status}`;
      const det = (json as { details?: string }).details;
      return { error: det ? `${err}: ${det}` : err };
    }
    return { data: json as T };
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      return { error: 'Request timed out — the agent may still be processing.' };
    }
    return { error: e instanceof Error ? e.message : 'Network error' };
  } finally {
    window.clearTimeout(timer);
  }
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return { raw: s };
  }
}

// ── Domain types (a strict subset of what the main frontend uses) ─────────

export interface Blueprint {
  blueprintId: string;
  name: string;
  description?: string;
  status?: string;
  agentConfig?: Record<string, unknown>;
}

export interface OrchestratorReply {
  synthesizedResponse: string;
  reasoning: string;
  selectedAgents: Array<{ blueprintId: string; name: string; description?: string }>;
  agentResponses: Array<{
    blueprintId: string;
    agentName: string;
    status: 'complete' | 'error';
    response: string;
    error?: string;
  }>;
  agentSessions: Record<string, string>;
}

export interface AgentChatReply {
  sessionId: string;
  message: string;
  timestamp: string;
}

export interface AutonomyChatReply {
  action: 'run_skill' | 'invoke_agent' | 'author_skill' | 'answer' | 'needs_credentials';
  reasoning?: string;
  reply?: string;
  skillId?: string;
  skillName?: string;
  agentId?: string;
  agentName?: string;
}

// ── Endpoints ─────────────────────────────────────────────────────────────

export function listBlueprints() {
  return request<{ blueprints: Blueprint[] }>('/blueprints');
}

export interface OfficeAddinAgent {
  blueprintId: string;
  agentId: string;
  status: 'deployed';
}

export async function getOfficeAddinAgent(blueprintId: string) {
  return request<OfficeAddinAgent>(
    `/office-addin/agents/${encodeURIComponent(blueprintId)}`,
  );
}

export function orchestratorChat(
  message: string,
  agentSessions: Record<string, string> = {},
  model = '',
) {
  return request<OrchestratorReply>('/orchestrator/chat', {
    method: 'POST',
    body: JSON.stringify({ message, agentSessions, model }),
  });
}

/** Brain chat — plans and runs skills / deployed agents or answers directly. */
export function autonomyChat(message: string, model = '') {
  return request<AutonomyChatReply>('/autonomy/chat', {
    method: 'POST',
    body: JSON.stringify({ message, model: model || '' }),
  });
}

export function agentChat(agentId: string, message: string, sessionId?: string) {
  return request<AgentChatReply>(`/agents/${encodeURIComponent(agentId)}/chat`, {
    method: 'POST',
    body: JSON.stringify({ message, sessionId }),
  });
}

/** Badge label for an autonomy Brain reply. */
export function autonomyBadge(data: AutonomyChatReply): string {
  if (data.skillName) return data.skillName;
  if (data.agentName) return data.agentName;
  if (data.action === 'author_skill') return 'New skill';
  return 'Brain';
}
